/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { createSelector } from 'reselect';

import { isCpd } from '@ibm-watson/shared/modules/ia/util/config';
import { ApiStatus } from '../../../ts';
import { FeatureFlag } from '../../../ts/feature-flags';
import type { RootState } from '../../../ts/state';
import { isFeatureEnabled } from '../../ia/store/features/selectors';
import {
  CONFIDENCE_THRESHOLD_VALUE,
  NUMBER_OF_RESULTS,
  RESPONSE_LENGTH_VALUE
} from '../util/constants';
import { isInstanceV2, filterFields } from '../util/search-skill';
import { discoveryAdapter } from './adapter';

const selectors = discoveryAdapter.getSelectors(
  (state: RootState) => state.discovery
);

export const selectAllInstances = selectors.selectAll;
export const selectInstanceById = selectors.selectById;

/**
 * Selector to test whether instances are loading
 * @param {Object} state The current state
 * @return {boolean} true if the instances are loading, otherwise false
 */
export const selectAreInstancesLoading = (state: RootState) => {
  return state.discovery?.listInstanceStatus === ApiStatus.LOADING;
};

/**
 * Selector to test whether projects are loading
 * @param {Object} state The current state
 * @return {boolean} true if the projects are loading, otherwise false
 */
export const selectAreProjectsLoading = (state: RootState) => {
  return state.discovery?.listProjectStatus === ApiStatus.LOADING;
};

/**
 * Returns the set of all V2 Discovery instances, filtering out V1
 * by calling util function isInstanceV2
 * @param {Object} state the redux state
 * @returns {Array} an array of instance objects, empty if none found
 */
export const selectAllV2Instances = createSelector(
  [selectAllInstances],
  instances => instances.filter(instance => isInstanceV2(instance) || isCpd)
);

export const selectUnavailableInstances = (state: RootState) => {
  return state.discovery?.unavailableInstances ?? [];
};

export const selectFields = createSelector(
  [(state: RootState) => state.discovery?.fields],
  fields => filterFields(fields) || []
);

export const selectFieldExamples = createSelector(
  [(state: RootState) => state.discovery?.fieldExamples],
  fieldExamples => fieldExamples || []
);

/**
 * Returns the projects array for the Discovery instance identified by instanceId
 * @param {Object} state the redux state
 * @param {String} instanceId id for target instance
 * @returns {Array} array of projects, may be empty, will be empty if instance is V1
 */
export const selectProjectsByInstanceId = (
  state: RootState,
  instanceId: string
) => {
  return selectInstanceById(state, instanceId)?.projects ?? [];
};

/**
 * Returns the project with projectId within the Discovery instance identified by instanceId
 * @param {Object} state the redux state
 * @param {String} instanceId id for target instance
 * @param {String} projectId id of the project
 * @returns {Array} array of projects, may be empty, will be empty if instance is V1
 */
export const selectProject = (
  state: RootState,
  instanceId: string,
  projectId: string
) => {
  return selectInstanceById(state, instanceId)?.projects?.find(
    proj => proj.project_id === projectId
  );
};

export const selectedProjectHasCollection = (state: RootState) => {
  const selectedProjectId = state?.discovery?.selectedProjectId;
  const selectedInstanceId = state?.discovery?.selectedInstanceId;
  const project = selectInstanceById(state, selectedInstanceId)?.projects?.find(
    proj => proj.project_id === selectedProjectId
  );
  return project?.collection_count > 0;
};

export const isShowDetailConfig = (state: RootState) => {
  return state.discovery?.showDetailConfig ?? false;
};

export const selectSelectedInstanceId = (state: RootState) => {
  return state.discovery?.selectedInstanceId;
};

export const selectSelectedProjectId = (state: RootState) => {
  return state.discovery?.selectedProjectId;
};

export const selectIsDirty = (state: RootState) => state.discovery.isDirty;
export const selectIsChangingProject = (state: RootState) =>
  state.discovery.isChangingProject;
export const selectIsForceInstanceList = (state: RootState) =>
  state.discovery.isForceInstanceList;
export const selectDraftSkillId = (state: RootState) =>
  state.discovery.draft?.skill_id;

export const selectDraftSearchSkill = (state: RootState) =>
  state.discovery?.draft;

// NOTE a pre-created search skill shell has empty search_settings
export const selectIsDraftUninitialized = (state: RootState) =>
  !state.discovery?.draft?.search_settings?.schema_mapping;

export const selectDraftMaxPrimaryResults = (state: RootState) =>
  state.discovery?.draft?.search_settings?.discovery?.max_primary_results ??
  NUMBER_OF_RESULTS.DEFAULT;

export const selectDraftIsRefinedResults = (state: RootState) =>
  !!state.discovery?.draft?.search_settings?.discovery?.confidence_threshold;

export const selectDraftIsHighlightAnswers = (state: RootState) =>
  state.discovery?.draft?.search_settings?.discovery?.find_answers ?? true;

export const selectDraftIsDisplayShowMore = (state: RootState) => {
  const mtr =
    state.discovery?.draft?.search_settings?.discovery?.max_total_results ?? 5;
  return mtr >= NUMBER_OF_RESULTS.SHOW_MORE_MAX;
};

export const selectDraftSuccessMessage = (state: RootState) =>
  state.discovery?.draft?.search_settings?.messages?.success ?? '';

export const selectDraftNoResultsMessage = (state: RootState) =>
  state.discovery?.draft?.search_settings?.messages?.no_result ?? '';

export const selectDraftConnectivityMessage = (state: RootState) =>
  state.discovery?.draft?.search_settings?.messages?.error ?? '';

export const selectDraftTitleOptionValue = (state: RootState) =>
  state.discovery?.draft?.search_settings?.schema_mapping?.title ?? '';

export const selectDraftBodyOptionValue = (state: RootState) =>
  state.discovery?.draft?.search_settings?.schema_mapping?.body ?? '';

export const selectDraftUrlOptionValue = (state: RootState) =>
  state.discovery?.draft?.search_settings?.schema_mapping?.url ?? '';

export const selectDraftCitationsTitleValue = (state: RootState) =>
  state.discovery?.draft?.search_settings?.conversational_search
    ?.citations_title ?? '';

export const selectDraftMaxCitationsValue = (state: RootState) =>
  state.discovery?.draft?.search_settings?.conversational_search?.citations
    ?.max_citations ?? -1;

export const selectDraftIsConversationalSearchEnabled = (state: RootState) =>
  state.discovery?.draft?.search_settings?.conversational_search?.enabled ??
  false;

export const selectDraftConversationalSearchRetrievalConfidenceThreshold = (
  state: RootState
) =>
  state.discovery?.draft?.search_settings?.conversational_search
    ?.search_confidence?.pre_gen_threshold ??
  CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftConversationalSearchConfidenceThreshold = (
  state: RootState
) =>
  state.discovery?.draft?.search_settings?.conversational_search
    ?.search_confidence?.threshold ?? CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftConversationalSearchResponseConfidenceThreshold = (
  state: RootState
) =>
  state.discovery?.draft?.search_settings?.conversational_search
    ?.search_confidence?.post_gen_threshold ??
  CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN;

export const selectDraftConversationalSearchMultiTurn = (state: RootState) =>
  state.discovery?.draft?.search_settings?.conversational_search?.multi_turn
    ?.enabled ?? false;

export const selectDraftConversationalSearchVerbosityOption = (
  state: RootState
) =>
  state.discovery?.draft?.search_settings?.conversational_search
    ?.response_length?.option ?? RESPONSE_LENGTH_VALUE.MODERATE;

export const selectDraftDiscoveryInstanceId = (state: RootState) =>
  state.discovery?.draft?.search_settings?.discovery?.instance_id ?? '';

export const selectDraftDiscoveryProjectId = (state: RootState) =>
  state.discovery?.draft?.search_settings?.discovery?.project_id ?? '';

const selectIsSapConversationalSearchEnabled = (state: RootState) =>
  isFeatureEnabled(state, FeatureFlag.SAP_CONVERSATIONAL_SEARCH);

// If Sap conversational search is enabled then there will not be a citations title value. However, if Sap
// conversational search is not enabled, and normal conversational search is enabled, then there needs to be a citations
// title value. Lastly, if normal conversational search is not enabled, then there does not need to be a citations title
// for the config to be valid.
const selectIsConversationalSearchConfigValid = (state: RootState) => {
  return (
    selectIsSapConversationalSearchEnabled(state) ||
    (selectDraftIsConversationalSearchEnabled(state)
      ? !!selectDraftCitationsTitleValue(state)
      : true)
  );
};

export const selectIsConfigValid = (state: RootState) => {
  return (
    !!selectDraftDiscoveryProjectId(state) &&
    !!selectDraftDiscoveryInstanceId(state) &&
    (!!selectDraftUrlOptionValue(state) ||
      !!selectDraftBodyOptionValue(state) ||
      !!selectDraftTitleOptionValue(state)) &&
    !!selectIsConversationalSearchConfigValid(state) &&
    // selector will return a default, use direct state check to ensure not blank
    //   note also the ui component restricts to "", 1, 2,.. 5
    !!state.discovery?.draft?.search_settings?.discovery?.max_primary_results
  );
};

export const selectDraftDiscoveryFilterValue = (state: RootState) =>
  state.discovery?.draft?.search_settings?.discovery?.filter;
